import { color } from '@ahamove/design-tokens';
import AppBar from '@ahamove/web-components/lib/AppBar';
import Button from '@ahamove/web-components/lib/Button';
import NavigationBar from '@ahamove/web-components/lib/NavigationBar';
import Toolbar from '@ahamove/web-components/lib/Toolbar';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { NextSeoProps } from 'next-seo';
import { NextSeo } from 'next-seo';
import type { ReactNode } from 'react';
import React from 'react';
import { useWebInAppStore } from '@/global-stores/useWebInAppStore';
import { WebInAppEvent, baseUrl } from '@/lib/constants';
import { MyReservationIcon } from '@/lib/icons';
import jsFunction from '@/lib/jsFunction';
import useIsMobile from '@/shared-hooks/useIsMobile';
import ElevationScroll from '../ElevationScroll';
import ElevationScrollHeaderMobile from '../ElevationScrollHeaderMobile';
import ButtonScrollTop from './ButtonScrollTop';
import Footer from './Footer';
import Header from './Header';

type ContainerProps = Omit<NextSeoProps, 'children'> & {
  children: ReactNode;
  header?: boolean;
  footer?: boolean;
  className?: string;
  root?: boolean;
};
export default function Container({
  children,
  header = true,
  footer = true,
  className,
  root = false,
  ...props
}: ContainerProps) {
  const { t } = useTranslation(['common']);
  const { asPath, query, back, pathname } = useRouter();
  const {
    title = String(t('common:page.rent_ev')),
    description = 'Dịch vụ thuê xe điện',
  } = props;
  const isMobile = useIsMobile();
  const { isWebInApp } = useWebInAppStore();

  React.useEffect(() => {
    if (isWebInApp) {
      jsFunction.postMessage(WebInAppEvent.HIDE_TOOLBAR);
    }
  }, [isWebInApp]);

  return (
    <>
      <NextSeo
        title={title}
        titleTemplate={`%s | ${process.env.NEXT_PUBLIC_APP_NAME}`}
        defaultTitle={process.env.NEXT_PUBLIC_APP_NAME}
        description={description}
        canonical={`${baseUrl}${asPath}`}
        openGraph={{
          url: `${baseUrl}${asPath}`,
          type: 'website',
          site_name: process.env.NEXT_PUBLIC_APP_NAME,
          description,
          title: title || process.env.NEXT_PUBLIC_APP_NAME,
          images: [
            {
              url: `${baseUrl}/static/images/thuexedien.png`,
              height: 1200,
              width: 800,
              alt: title || process.env.NEXT_PUBLIC_APP_NAME,
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
          site: '@ahamove',
        }}
      />
      {!header || (isWebInApp && isMobile) ? null : <Header />}
      {isWebInApp && isMobile ? (
        <>
          {root ? (
            <ElevationScrollHeaderMobile>
              <NavigationBar
                closePosition="left"
                onClose={() => jsFunction.postMessage(WebInAppEvent.CLOSE)}
                extraActions={
                  <Link href={{ pathname: '/bookings', query }}>
                    <Button
                      size="small"
                      color="inherit"
                      rounded
                      startIcon={<MyReservationIcon />}
                      sx={{
                        bgcolor: 'common.white',
                        color: color.blue[70],
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      }}
                      data-test-id="goToBookingPage"
                    >
                      {t('common:page.my_bookings')}
                    </Button>
                  </Link>
                }
              />
            </ElevationScrollHeaderMobile>
          ) : (
            <>
              <ElevationScroll>
                <AppBar color="inherit">
                  <NavigationBar
                    title={title}
                    onBack={() => {
                      if (pathname === '/booking/[id]') {
                        return;
                      }
                      back();
                    }}
                  />
                </AppBar>
              </ElevationScroll>
              <Toolbar sx={{ minHeight: '48px !important' }} />
            </>
          )}
        </>
      ) : null}
      <div
        className={cn(
          'desktop:min-h-content',
          {
            'desktop:mt-header-desktop': header,
            'mt-header-mobile': header && !isWebInApp,
          },
          className
        )}
      >
        {children}
      </div>
      {isMobile ? null : <ButtonScrollTop />}
      {!footer ? null : <Footer />}
    </>
  );
}
